import React, { useCallback, useEffect, useRef, useState } from 'react';
import Flag from 'react-flagkit';
import { FieldValues } from 'react-hook-form';
import PhoneInput from 'react-phone-number-input/input';
import cn from 'classnames';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { InputType } from 'zlib';

import { Icons } from '@icons/icons';

import { removeSpecialCharacters } from '@utils/removeSpecialCharacters';

import { PhoneNumber } from 'libphonenumber-js/types';
import { EInputType, IInputProps } from './Input.types';

import { regExpDecimal, regExpNumbers } from '@constants/regExp';

import Guideline from '../Guideline';
import Label from '../Label';
import Meta from '../Meta';

import './Input.scss';

const INPUT_TYPE_LIST: InputType[] = [EInputType.float, EInputType.integer];

const Input = <T extends FieldValues>({
	className,
	containerClassName,
	disabled,
	errorMessage,
	isError = false,
	isPhoneNumber,
	isReadOnly = false,
	label,
	labelAction,
	labelActionDisabled,
	labelActionText,
	labelActionTooltip,
	maxLength,
	isMaxLengthCounter = false,
	infoTooltip,
	name,
	onBlur,
	onChange,
	onEnterKeyDown,
	onFocus,
	placeholder = '',
	regexValidation,
	register,
	required,
	setValue,
	tooltipContent,
	tooltipPlacement,
	type = EInputType.text,
	value,
	valueLength,
	withCustomValidation,
	withUseForms = true,
	onPaste,
}: IInputProps<T>) => {
	const isURL = type === EInputType.url;
	const isPrice = type === EInputType.price;
	const isPassword = type === EInputType.password;
	const containerClasses = cn('f5gInputContainer', containerClassName);
	const inputClasses = cn('f5gInput', className, {
		'f5gInputUrl': isURL || isPrice || isPhoneNumber,
		'f5gInputError': !!errorMessage || isError,
		'f5gInputPasswordType': isPassword,
		'f5gReadOnly': isReadOnly,
		'f5gInputWrapperWithDisableStyle': !!disabled,
	});

	const [isFocus, setIsFocus] = useState(false);
	const [isVisiblePassword, setIsVisiblePassword] = useState(false);
	const [countryCode, setCountryCode] = useState<string | undefined>('');

	const passwordInputType = isVisiblePassword ? EInputType.text : EInputType.password;

	const toggleVisiblePassword = () => setIsVisiblePassword(prevState => !prevState);

	useEffect(() => {
		if (!isPhoneNumber || !value) return;

		const phoneNumber: PhoneNumber = parsePhoneNumberFromString(value!.toString()) as PhoneNumber;

		setCountryCode(phoneNumber?.country);
	}, [value, isPhoneNumber]);

	const onFocusHandler = () => {
		setIsFocus(true);

		if (onFocus) onFocus(name, value);
	};

	const onBlurHandler = () => {
		setIsFocus(false);

		if (onBlur) onBlur(name, value);
	};

	const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		if ((regexValidation || INPUT_TYPE_LIST.includes(type)) && setValue) {
			const regex: RegExp = regexValidation || (
				type === EInputType.integer
					? regExpNumbers
					: regExpDecimal
			);

			if (!withCustomValidation) {
				removeSpecialCharacters(e.target.value, name, setValue, regex);
			} else {
				setValue(name, e.target.value);
			}
		} else if (onChange) {
			onChange(e.target.value);
		}
	}, [name, onChange, regexValidation, setValue, type, withCustomValidation]);

	const handleChangePhone = (phone?: string) => onChange && onChange(phone || '');

	const inputRef = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		if (inputRef.current && onEnterKeyDown) {
			const handleEnterKeyDown = (e: KeyboardEvent) => {
				if (e.key !== 'Enter') {
					return;
				}

				e.preventDefault();
				e.stopPropagation();
				onEnterKeyDown((e.target as HTMLInputElement).value);
			};
			inputRef.current.addEventListener('keydown', handleEnterKeyDown);
		}
	}, [onEnterKeyDown]);

	return (
		<div className={containerClasses}>
			<div className='f5gInputHeader'>
				{label && (
					<Label
						className='f5gInputLabel'
						label={label}
						labelAction={labelAction}
						labelActionDisabled={labelActionDisabled}
						labelActionText={labelActionText}
						labelActionTooltip={labelActionTooltip}
						required={required}
						tooltipContent={tooltipContent}
						tooltipPlacement={tooltipPlacement}
					/>
				)}
				{infoTooltip && (
					<Guideline
						guidelineText={infoTooltip}
						placement='top'
					/>
				)}
			</div>
			<div className={cn('f5gInputWrapper', { f5gInputWrapperWithDisableStyle: !isPrice })}>
				{isURL && <div className='f5gInputUrlType'>https://</div>}
				{isPrice && (
					<div className={cn('f5gInputUrlType', { f5gInputPriceTypeDisabled: disabled })}>
						£
					</div>
				)}
				{isPhoneNumber && (
					<div className={cn('f5gInputPhoneType', {
						f5gInputPhoneTypeFocus: isFocus,
						f5gInputPhoneTypeError: !!errorMessage || isError,
						f5gInputPhoneTypeDisabled: disabled,
					})}>
						{countryCode
							? <Flag country={countryCode} />
							: Icons.phoneIcon()
						}
					</div>
				)}
				{isPhoneNumber ? (
					<PhoneInput
						aria-label={name}
						className={inputClasses}
						disabled={disabled}
						onBlur={onBlurHandler}
						onChange={handleChangePhone}
						onFocus={onFocusHandler}
						placeholder={placeholder}
						value={value as string}
					/>
				) : withUseForms ? (
					<input
						className={inputClasses}
						disabled={disabled}
						maxLength={maxLength}
						placeholder={placeholder}
						type={isPassword ? passwordInputType : type}
						{...(register ? register(name) : {})}
						aria-label={name}
						autoComplete='new-password'
						onBlur={(e) => {
							onBlurHandler();

							if (register) register(name)?.onBlur(e);
						}}
						onFocus={onFocusHandler}
						onInput={handleChange}
						onPaste={onPaste}
						readOnly={isReadOnly}
					/>
				) : (
					<input
						ref={inputRef}
						aria-label={name}
						className={inputClasses}
						disabled={disabled}
						id={name}
						maxLength={maxLength}
						name={name}
						onBlur={onBlurHandler}
						onChange={handleChange}
						onFocus={onFocusHandler}
						placeholder={placeholder}
						readOnly={isReadOnly}
						type={isPassword ? passwordInputType : type}
						value={value as string}
					/>
				)}
				{isPassword &&
					<div
						className='f5gInputPasswordIcon'
						onClick={toggleVisiblePassword}
					>
						{isVisiblePassword ? Icons.eyeOpen() : Icons.eyeClose()}
					</div>
				}
			</div>
			<div className='f5gInputMetaContainer'>
				{errorMessage && (
					<Meta
						className='f5gInputMeta'
						message={errorMessage as string}
					/>
				)}
				{isMaxLengthCounter && maxLength
					&& <div className={cn('f5gInputCharCounter', {
						'f5gInputCharCounterError': valueLength && valueLength > maxLength,
					})}>
						{`${valueLength || 0}/${maxLength}`}
					</div>
				}
			</div>
		</div>
	);
};

export default Input;
